// Loads Cloudflare Turnstile and resolves a token for POST /session (architecture Section 7:
// "Turnstile on session start" — the only Session API call that needs one, per
// design/2026-08-26-session-api-contract.md). Kept separate from api-client.ts: this is external
// script loading and widget lifecycle, a different concern from the fetch wrapper.

const SCRIPT_SRC = "https://challenges.cloudflare.com/turnstile/v0/api.js";

// Real widget site key (checklist Section 2, Managed mode, boardroomforcreatives.com). Public by
// design — Turnstile site keys are not secrets. Overridable via the embed's `turnstile-site-key`
// attribute for a different deployment/testing key (Cloudflare's 3x00000000000000000000FF test
// key always forces the interactive challenge, which is how the path below gets exercised).
const DEFAULT_SITE_KEY = "0x4AAAAAAEc-ULnwUfG2rEoj";

/** How long to wait for a silent (non-interactive) token, script load included. Ordinarily a few
 * seconds. Suspended once Turnstile asks the visitor to interact: from then on Turnstile's own
 * `timeout-callback` bounds the wait, and the visitor can always close the panel. */
const NON_INTERACTIVE_TIMEOUT_MS = 30_000;

/** Above the overlay (`--bfc-z-overlay`, 2147483000) so an interactive challenge is never hidden
 * behind our own panel. */
const WIDGET_Z_INDEX = "2147483600";

declare global {
  interface Window {
    turnstile?: {
      render: (
        container: HTMLElement,
        options: {
          sitekey: string;
          appearance?: "always" | "execute" | "interaction-only";
          callback: (token: string) => void;
          "error-callback"?: () => void;
          "timeout-callback"?: () => void;
          "unsupported-callback"?: () => void;
          "before-interactive-callback"?: () => void;
        }
      ) => string;
      remove: (widgetId: string) => void;
    };
  }
}

let scriptLoad: Promise<void> | null = null;

function loadScript(): Promise<void> {
  if (window.turnstile) return Promise.resolve();
  if (scriptLoad) return scriptLoad;

  scriptLoad = new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src = SCRIPT_SRC;
    script.async = true;
    script.addEventListener("load", () => resolve());
    script.addEventListener("error", () => {
      scriptLoad = null; // let a later attempt retry instead of reusing the failure
      script.remove();
      reject(new Error("Failed to load Turnstile script"));
    });
    document.head.appendChild(script);
  });
  return scriptLoad;
}

export interface TurnstileOptions {
  siteKey?: string;
  /** Aborting (the visitor closed the panel) removes the widget and rejects. */
  signal?: AbortSignal;
  /** Called when Turnstile is about to show an interactive challenge, so the panel can tell the
   * visitor to complete it. */
  onInteractive?: () => void;
}

/**
 * Renders a Turnstile widget and resolves its token. `interaction-only` keeps the widget invisible
 * for the usual silent pass, and shows it only when Managed mode escalates to an interactive
 * challenge. Its container must stay visible and clickable for that case: the original version
 * pinned it at `opacity: 0; pointer-events: none`, which left escalated visitors on "Just a
 * moment." forever (incident 2026-10-09, docs/plans/2026-10-09-session-start-incident-fixes.md).
 * Every exit path settles the promise and removes the widget.
 */
export function getTurnstileToken(options: TurnstileOptions = {}): Promise<string> {
  const { siteKey = DEFAULT_SITE_KEY, signal, onInteractive } = options;

  return new Promise((resolve, reject) => {
    let widgetId: string | null = null;
    let settled = false;
    const container = document.createElement("div");
    Object.assign(container.style, {
      position: "fixed",
      left: "50%",
      bottom: "24px",
      transform: "translateX(-50%)",
      zIndex: WIDGET_Z_INDEX,
    });

    const cleanup = () => {
      clearTimeout(timer);
      signal?.removeEventListener("abort", onAbort);
      if (widgetId !== null) window.turnstile?.remove(widgetId);
      container.remove();
    };
    const succeed = (token: string) => {
      if (settled) return;
      settled = true;
      cleanup();
      resolve(token);
    };
    const fail = (message: string) => {
      if (settled) return;
      settled = true;
      cleanup();
      reject(new Error(message));
    };
    const onAbort = () => fail("Turnstile aborted");

    const timer = setTimeout(() => fail("Turnstile timed out"), NON_INTERACTIVE_TIMEOUT_MS);
    if (signal?.aborted) return onAbort();
    signal?.addEventListener("abort", onAbort);

    loadScript()
      .then(() => {
        if (settled) return;
        if (!window.turnstile) return fail("Turnstile script loaded but window.turnstile is unavailable");
        document.body.appendChild(container);
        widgetId = window.turnstile.render(container, {
          sitekey: siteKey,
          appearance: "interaction-only",
          callback: succeed,
          "error-callback": () => fail("Turnstile verification failed"),
          "timeout-callback": () => fail("Turnstile interactive challenge timed out"),
          "unsupported-callback": () => fail("Turnstile unsupported in this browser"),
          "before-interactive-callback": () => {
            clearTimeout(timer);
            onInteractive?.();
          },
        });
      })
      .catch((err: unknown) => fail(err instanceof Error ? err.message : "Failed to load Turnstile"));
  });
}
